import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  ISalesFunnel,
  ISalesFunnelStage,
  ISalesFunnelClosedReasons,
  ISalesFunnelEditDTO,
  ISalesFunnelCreateModel,
} from './typings'
import { ServiceConfig } from '../services/config'
import { get, post, put, remove, APIReject, APIError } from '../services/crud'
import { StoreData } from '../reducers/typings'
import { ENTITY_TYPES } from '@sales-center/common'
import { loadFilterPreferences } from '../userPreferences/api'

const baseUrl = `${ServiceConfig.baseAPIUrl}/SalesFunnel`

export const getSalesFunnelList = createAsyncThunk<
  ISalesFunnel[],
  boolean,
  { rejectValue: APIReject }
>('salesFunnels/all', async (includeCounts, thunkApi) => {
  const response = await get(`${baseUrl}/all?includeCounts=${includeCounts}`)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue((await response.json()) as APIReject)
})

export const createSalesFunnel = createAsyncThunk<
  ISalesFunnel,
  ISalesFunnelCreateModel,
  { state: StoreData; rejectValue: APIReject }
>('salesFunnel/create', async (funnel, thunkApi) => {
  const response = await post(baseUrl, funnel)

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue((await response.json()) as APIReject)
})

export const updateSalesFunnel = createAsyncThunk<
  ISalesFunnel,
  ISalesFunnelCreateModel,
  { state: StoreData; rejectValue: APIReject }
>('salesFunnel/update', async (funnel, thunkApi) => {
  const response = await put(baseUrl, funnel)

  if (response.ok) {
    thunkApi.dispatch(loadFilterPreferences())
    return await response.json()
  }

  return thunkApi.rejectWithValue((await response.json()) as APIReject)
})

export const deleteSalesFunnel = createAsyncThunk<
  string,
  string,
  { rejectValue: APIError }
>('salesFunnel/delete', async (id, thunkApi) => {
  const response = await remove(`${baseUrl}/${id}`)

  if (response.ok) {
    thunkApi.dispatch(loadFilterPreferences())
    return id
  }

  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

export const setDefaultSalesFunnel = createAsyncThunk<
  string,
  string,
  { rejectValue: APIReject }
>('salesFunnel/setDefault', async (id, thunkApi) => {
  const response = await post(`${baseUrl}/setDefault/${id}`)
  if (response.ok) return id
  return thunkApi.rejectWithValue((await response.json()) as APIReject)
})

export const getAllFunnelStages = createAsyncThunk<
  ISalesFunnelStage[],
  void,
  { rejectValue: APIReject }
>('salesFunnel/stages/all', async (_, thunkApi) => {
  const response = await get(`${baseUrl}/stages`)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue((await response.json()) as APIReject)
})

export const getEntityClosedReasons = createAsyncThunk<
  ISalesFunnelClosedReasons,
  { salesFunnelId: number; entityType: ENTITY_TYPES },
  { rejectValue: APIReject }
>('salesFunnel/closedReasons', async (input, thunkApi) => {
  const { entityType, salesFunnelId } = input

  const path =
    entityType === ENTITY_TYPES.Lead ? 'leadClosedReasons' : 'oppClosedReasons'

  const response = await get(`${baseUrl}/${path}/${salesFunnelId}`)

  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue((await response.json()) as APIReject)
})

export const getSalesFunnelForEdit = createAsyncThunk<
  ISalesFunnelEditDTO,
  string,
  { rejectValue: APIReject }
>('salesFunnel/edit/id', async (id, thunkApi) => {
  const response = await get(`${baseUrl}/${id}`)
  if (response.ok) return await response.json()
  return thunkApi.rejectWithValue((await response.json()) as APIReject)
})
